import React from 'react'
import styled, { css } from 'styled-components'
import Typography, { TYPOGRAPHY_ICON_SIZE_CSS_VAR, TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from './Typography'
import cn from 'clsx'
import { rem } from 'polished'
import { setCSSVar } from 'lib/theme/themeUtils'

export const BodyTextLarge = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '24px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(20))}
  font-size: 16px;
`

export const BodyTextMedium = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '20px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(20))}
  font-size: 14px;
`

export const BodyTextSmall = css`
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '16px')}
  ${setCSSVar(TYPOGRAPHY_ICON_SIZE_CSS_VAR, rem(16))}
  font-size: 12px;
`

/*
  Font sizes are expressed in px as they are not to be affected by the
  20% REM font decrease at mobile sizes. These font sizes are absolute sizes.
*/
export const BodyTextElement = styled(Typography)`
  font-family: ${props => props.theme.font.primary.family};

  &.variant-large {
    ${BodyTextLarge}
  }

  &.variant-large:is(small),
  &.variant-large small,
  &.variant-medium {
    ${BodyTextMedium}
  }

  &.variant-medium:is(small),
  &.variant-medium small,
  &.variant-small,
  &.variant-small:is(small),
  &.variant-small small {
    ${BodyTextSmall}
  }

  &.weight-normal { font-weight: ${props => props.theme.font.primary.weight.regular}; }
  &.weight-bold, &:is(b, strong), b, strong { font-weight: ${props => props.theme.font.primary.weight.superBold}; }
  &.italics, &:is(i, em), i, em { font-style: italic; }
  &.underline { text-decoration: underline; }
  &.strike-through, &:is(s), s { text-decoration: line-through; }
`

interface Props extends React.ComponentProps<typeof Typography> {
  variant: 'small' | 'medium' | 'large';
  weight?: 'normal' | 'bold';
  italics?: boolean;
  underline?: boolean;
  strikeThrough?: boolean;
}

function BodyText(props: Props) {
  const {
    variant,
    weight,
    italics,
    underline,
    strikeThrough,
    as,
    className,
    ...rest
  } = props

  return <BodyTextElement
    {...rest}
    forwardedAs={as}
    className={cn(
      className,
        `variant-${variant}`,
        weight ? `weight-${weight}` : undefined,
        {
          italics,
          underline,
          'strike-through': strikeThrough,
        },
    )}
  />
}

export default BodyText
